import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { History, ChevronDown, ChevronRight, ArrowUpDown, RefreshCw, Search, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-amber-100 text-amber-700",
  delete: "bg-red-100 text-red-700",
};

const ENTITY_LABELS = {
  requirement: "Requirement",
  goal: "Goal",
  milestone: "Milestone",
  pain_point: "Pain Point",
  uat: "UAT",
  tracking: "Tracking",
  stakeholder: "Stakeholder",
  feedback: "Feedback",
  gonogo: "Go/No-Go",
  user: "User",
  product: "Product",
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const ChangeRow = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChanges = entry.changes && entry.changes.length > 0;

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`changelog-row-${entry.id}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 transition-colors cursor-pointer"
        onClick={() => hasChanges && setExpanded(!expanded)}
      >
        <div className="flex-shrink-0 w-5">
          {hasChanges ? (
            expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
          ) : <span className="w-3.5" />}
        </div>
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${ACTION_COLORS[entry.action] || "bg-slate-100 text-slate-600"}`}>
          {entry.action}
        </span>
        <span className="text-xs font-medium text-slate-500 w-24 flex-shrink-0">
          {ENTITY_LABELS[entry.entity_type] || entry.entity_type}
        </span>
        <span className="text-sm text-slate-800 font-medium truncate flex-1" title={entry.entity_name}>
          {entry.entity_name || "—"}
        </span>
        {entry.product_name && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 flex-shrink-0 max-w-[160px] truncate" title={entry.product_name}>
            {entry.product_name}
          </span>
        )}
        <span className="text-xs text-slate-400 flex-shrink-0 w-32 text-right">{entry.user_name}</span>
        <span className="text-[11px] text-slate-400 flex-shrink-0 w-44 text-right tabular-nums">{formatTimestamp(entry.timestamp)}</span>
      </div>
      {expanded && hasChanges && (
        <div className="mx-9 mb-3 bg-slate-50 rounded-lg border border-slate-200/80 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100/70 text-slate-500">
                <th className="text-left px-3 py-1.5 font-medium w-36">Field</th>
                <th className="text-left px-3 py-1.5 font-medium">Previous Value</th>
                <th className="text-left px-3 py-1.5 font-medium">New Value</th>
              </tr>
            </thead>
            <tbody>
              {entry.changes.map((ch, i) => (
                <tr key={i} className="border-t border-slate-200/60">
                  <td className="px-3 py-1.5 text-slate-600 font-medium capitalize">{ch.field?.replace(/_/g, " ")}</td>
                  <td className="px-3 py-1.5 text-red-600/80 break-all max-w-[300px]">
                    <span className="bg-red-50 px-1 rounded">{ch.old || "—"}</span>
                  </td>
                  <td className="px-3 py-1.5 text-emerald-700/80 break-all max-w-[300px]">
                    <span className="bg-emerald-50 px-1 rounded">{ch.new || "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function ChangeLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ entity_type: "", action: "", user_name: "" });
  const [filterOptions, setFilterOptions] = useState({ users: [], entity_types: [], products: [] });
  const [page, setPage] = useState(0);
  const LIMIT = 100;

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/changelog/filters`);
      setFilterOptions(res.data);
    } catch {}
  }, []);

  const fetchEntries = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: pg * LIMIT };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      if (filters.user_name) params.user_name = filters.user_name;
      const res = await axios.get(`${API}/api/changelog`, { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch { setEntries([]); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { setPage(0); fetchEntries(0); }, [fetchEntries]);

  return (
    <div className="max-w-7xl mx-auto" data-testid="changelog-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
            <History className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Change Log</h1>
            <p className="text-xs text-slate-400">{total} total entries across all products</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => { fetchFilters(); fetchEntries(page); }} data-testid="changelog-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-slate-200/80 shadow-sm">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <Select value={filters.entity_type || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, entity_type: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="changelog-filter-entity">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {filterOptions.entity_types.map((t) => (
                  <SelectItem key={t} value={t}>{ENTITY_LABELS[t] || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.action || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, action: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="changelog-filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Filter by user..."
                value={filters.user_name}
                onChange={(e) => setFilters((f) => ({ ...f, user_name: e.target.value }))}
                className="h-8 w-44 text-xs pl-7"
                data-testid="changelog-filter-user"
              />
            </div>
            {(filters.entity_type || filters.action || filters.user_name) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => setFilters({ entity_type: "", action: "", user_name: "" })}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200/80 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2 bg-slate-50/80 border-b border-slate-200/80 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          <span className="w-5" />
          <span className="w-16">Action</span>
          <span className="w-24">Type</span>
          <span className="flex-1">Name</span>
          <span className="w-[160px]">Product</span>
          <span className="w-32 text-right">User</span>
          <span className="w-44 text-right">Timestamp</span>
        </div>
        <ScrollArea className="max-h-[calc(100vh-280px)]">
          {loading && entries.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <History className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">No changes recorded yet</p>
              <p className="text-xs mt-1">Changes will appear here as users create, update, or delete items</p>
            </div>
          ) : (
            entries.map((entry) => <ChangeRow key={entry.id} entry={entry} />)
          )}
        </ScrollArea>
        {total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200/80 bg-slate-50/50">
            <span className="text-xs text-slate-400">Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page === 0} onClick={() => { setPage(page - 1); fetchEntries(page - 1); }}>Prev</Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={(page + 1) * LIMIT >= total} onClick={() => { setPage(page + 1); fetchEntries(page + 1); }}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
